import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Check, ArrowRight, Phone, User, Mail, FileText, Stethoscope, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time slots
const timeSlots = [
  '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM'
];

// Departments
const departmentOptions = [
  { id: 'dept-outpatient', name: 'Outpatient Services' },
  { id: 'dept-inpatient', name: 'Inpatient Services' },
  { id: 'dept-maternal', name: 'Maternal and Child Health' },
  { id: 'dept-diagnostic', name: 'Diagnostic Services' },
  { id: 'dept-emergency', name: 'Emergency Services' },
  { id: 'dept-pharmacy', name: 'Pharmacy' },
  { id: 'dept-laboratory', name: 'Laboratory' },
  { id: 'dept-general', name: 'General Medicine' },
  { id: 'dept-surgical', name: 'Surgical Services' },
  { id: 'dept-specialized', name: 'Specialized Clinics' }
];

export default function BookAppointment() {
  const [departments, setDepartments] = useState(departmentOptions);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    department_id: '',
    department_name: '',
    doctor_id: '',
    doctor_name: '',
    date: null,
    time_slot: '',
    reason: '',
    message: ''
  });

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API}/departments`);
        if (response.data && response.data.length > 0) {
          setDepartments(response.data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (formData.department_id) {
      const fetchDoctors = async () => {
        try {
          const response = await axios.get(`${API}/doctors?department_id=${formData.department_id}`);
          setDoctors(response.data || []);
        } catch (error) {
          console.error('Error fetching doctors:', error);
          setDoctors([]);
        }
      };
      fetchDoctors();
    } else {
      setDoctors([]);
    }
  }, [formData.department_id]);

  const handleDepartmentChange = (value) => {
    const dept = departments.find(d => d.id === value);
    setFormData({
      ...formData,
      department_id: value,
      department_name: dept?.name || '',
      doctor_id: '',
      doctor_name: ''
    });
  };

  const handleDoctorChange = (value) => {
    const doc = doctors.find(d => d.id === value);
    setFormData({
      ...formData,
      doctor_id: value,
      doctor_name: doc?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.patient_name || !formData.patient_email || !formData.patient_phone || 
        !formData.department_id || !formData.date || !formData.time_slot || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd')
      };
      
      await axios.post(`${API}/appointments`, payload);
      setSubmitted(true);
      toast.success('Appointment request submitted successfully!');
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit appointment request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      patient_name: '',
      patient_email: '',
      patient_phone: '',
      department_id: '',
      department_name: '',
      doctor_id: '',
      doctor_name: '',
      date: null,
      time_slot: '',
      reason: '',
      message: ''
    });
  };

  if (submitted) {
    return (
      <div>
        <section className="bg-gradient-to-br from-primary via-primary to-teal-dark py-20 md:py-28" data-testid="booking-success-hero">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-24 h-24 bg-accent rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
                <Check className="w-12 h-12 text-white" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Appointment Request Submitted!
              </h1>
              <p className="text-white/80 text-lg">
                Thank you for choosing Baobab Hospital. We will contact you shortly to confirm your appointment.
              </p>
            </div>
          </div>
        </section>
        
        <section className="section-spacing" data-testid="booking-success-details">
          <div className="container-custom">
            <Card className="max-w-2xl mx-auto shadow-float">
              <CardHeader className="border-b">
                <CardTitle className="text-primary font-heading text-xl">Appointment Details</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Patient Name</p>
                    <p className="font-semibold text-primary">{formData.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Phone Number</p>
                    <p className="font-semibold text-primary">{formData.patient_phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Email Address</p>
                    <p className="font-semibold text-primary">{formData.patient_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Department</p>
                    <p className="font-semibold text-primary">{formData.department_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Preferred Date</p>
                    <p className="font-semibold text-primary">{format(formData.date, 'MMMM dd, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Preferred Time</p>
                    <p className="font-semibold text-primary">{formData.time_slot}</p>
                  </div>
                  {formData.doctor_name && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Preferred Doctor</p>
                      <p className="font-semibold text-primary">{formData.doctor_name}</p>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <p className="text-sm text-slate-500 mb-1">Reason for Visit</p>
                    <p className="font-semibold text-primary">{formData.reason}</p>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t flex flex-col sm:flex-row gap-4">
                  <Button onClick={resetForm} className="btn-primary flex-1" data-testid="book-another-btn">
                    Book Another Appointment
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => window.print()}>
                    Print Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-teal-dark py-20 md:py-28" data-testid="booking-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">Book an Appointment</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Schedule Your Visit
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Book an appointment with our experienced doctors at Baobab Hospital. Fill out the form below and we will contact you to confirm your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-spacing" data-testid="booking-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-float">
                <CardHeader className="border-b bg-warm/50">
                  <CardTitle className="text-primary font-heading text-xl flex items-center gap-3">
                    <CalendarIcon className="w-6 h-6 text-accent" />
                    Appointment Request Form
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h3 className="font-heading font-semibold text-primary text-lg mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-accent" />
                        Personal Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="patient_name">Full Name <span className="text-red-500">*</span></Label>
                          <Input
                            id="patient_name"
                            value={formData.patient_name}
                            onChange={(e) => setFormData({...formData, patient_name: e.target.value})}
                            placeholder="Enter your full name"
                            required
                            className="h-12"
                            data-testid="booking-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="patient_phone">Phone Number <span className="text-red-500">*</span></Label>
                          <Input
                            id="patient_phone"
                            value={formData.patient_phone}
                            onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                            placeholder="+255 XXX XXX XXX"
                            required
                            className="h-12"
                            data-testid="booking-phone-input"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="patient_email">Email Address <span className="text-red-500">*</span></Label>
                          <Input
                            id="patient_email"
                            type="email"
                            value={formData.patient_email}
                            onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
                            placeholder="Enter your email address"
                            required
                            className="h-12"
                            data-testid="booking-email-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Appointment Details */}
                    <div>
                      <h3 className="font-heading font-semibold text-primary text-lg mb-4 flex items-center gap-2">
                        <Stethoscope className="w-5 h-5 text-accent" />
                        Appointment Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Department <span className="text-red-500">*</span></Label>
                          <Select value={formData.department_id} onValueChange={handleDepartmentChange}>
                            <SelectTrigger className="h-12" data-testid="booking-department-select">
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                            <SelectContent>
                              {departments.map((dept) => (
                                <SelectItem key={dept.id} value={dept.id}>
                                  {dept.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Doctor</Label>
                          <Select 
                            value={formData.doctor_id} 
                            onValueChange={handleDoctorChange} 
                            disabled={!formData.department_id || doctors.length === 0}
                          >
                            <SelectTrigger className="h-12" data-testid="booking-doctor-select">
                              <SelectValue placeholder={formData.department_id ? (doctors.length > 0 ? "Select doctor" : "No doctors available") : "Select department first"} />
                            </SelectTrigger>
                            <SelectContent>
                              {doctors.map((doc) => (
                                <SelectItem key={doc.id} value={doc.id}>
                                  {doc.name} - {doc.specialty}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Date <span className="text-red-500">*</span></Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full h-12 justify-start text-left font-normal"
                                data-testid="booking-date-picker"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.date ? format(formData.date, 'PPP') : <span className="text-slate-500">Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.date}
                                onSelect={(date) => setFormData({...formData, date})}
                                disabled={(date) => date < new Date() || date.getDay() === 0}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Time <span className="text-red-500">*</span></Label>
                          <Select 
                            value={formData.time_slot} 
                            onValueChange={(value) => setFormData({...formData, time_slot: value})}
                          >
                            <SelectTrigger className="h-12" data-testid="booking-time-select">
                              <Clock className="mr-2 h-4 w-4" />
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((slot) => (
                                <SelectItem key={slot} value={slot}>
                                  {slot}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Reason & Message */}
                    <div>
                      <h3 className="font-heading font-semibold text-primary text-lg mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-accent" />
                        Visit Information
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="reason">Reason for Visit <span className="text-red-500">*</span></Label>
                          <Input
                            id="reason"
                            value={formData.reason}
                            onChange={(e) => setFormData({...formData, reason: e.target.value})}
                            placeholder="e.g., General checkup, Follow-up visit, Consultation"
                            required
                            className="h-12"
                            data-testid="booking-reason-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="message">Additional Message (Optional)</Label>
                          <Textarea
                            id="message"
                            value={formData.message}
                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                            placeholder="Any additional information you would like to share with us..."
                            rows={4}
                            data-testid="booking-message-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <Button 
                        type="submit" 
                        className="w-full bg-accent hover:bg-coral-soft text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all" 
                        disabled={loading}
                        data-testid="booking-submit-btn"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Book Appointment
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                      <p className="text-center text-sm text-slate-500 mt-4">
                        By submitting this form, you agree to our terms and privacy policy.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Need Help Card */}
              <Card className="bg-warm border-0">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-primary text-lg mb-4">Need Help?</h3>
                  <p className="text-slate-600 mb-4">
                    If you need assistance booking your appointment or have any questions, please contact us.
                  </p>
                  <div className="space-y-3">
                    <a href="tel:+255659984488" className="flex items-center gap-3 text-primary hover:text-accent transition-colors">
                      <Phone className="w-5 h-5" />
                      <span className="font-medium">+255 659 984 488</span>
                    </a>
                    <a href="tel:+255760215642" className="flex items-center gap-3 text-primary hover:text-accent transition-colors">
                      <Phone className="w-5 h-5" />
                      <span className="font-medium">+255 760 215 642</span>
                    </a>
                    <a href="tel:+255780635888" className="flex items-center gap-3 text-primary hover:text-accent transition-colors">
                      <Phone className="w-5 h-5" />
                      <span className="font-medium">+255 780 635 888</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Operating Hours Card */}
              <Card className="bg-gradient-to-br from-primary to-teal-dark text-white overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-accent" />
                    Operating Hours
                  </h3>
                  <div className="text-center py-4">
                    <p className="text-2xl font-bold text-accent mb-2">24/7</p>
                    <p className="text-white/90 text-sm">Open 24 Hours</p>
                    <p className="text-white/90 text-sm">7 Days a Week</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 text-center">
                    <p className="text-sm text-white/90">
                      <span className="font-semibold text-accent">Hospital Services:</span><br />
                      Always available for your healthcare needs
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Location Card */}
              <Card className="bg-white shadow-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-primary text-lg mb-4">Our Location</h3>
                  <p className="text-slate-600 text-sm">
                    <strong>Baobab Hospital (BHSP)</strong><br />
                    Mapinga – Bagamoyo, Pwani<br />
                    P.O. Box 35692
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
